import { useMemo, useEffect, useRef } from "react";

export const useViewerPeer = ({ pcRef, onRemoteStream, onIceCandidate }) => {
  // ✅ FIX: Removed internal pcRef. Now uses the prop from parent (useCallSocket)
  const answerQueueRef = useRef([]);
  
  // Keep callbacks fresh without recreating createPC
  const callbacksRef = useRef({ onRemoteStream, onIceCandidate });
  useEffect(() => {
    callbacksRef.current = { onRemoteStream, onIceCandidate };
  }, [onRemoteStream, onIceCandidate]);

  const createPC = (iceConfig) => {
    // ♻️ REUSE: If PC exists and is active, return it.
    if (pcRef.current && pcRef.current.signalingState !== "closed") {
       return pcRef.current;
    }

    console.log("🛠️ Creating new Viewer PeerConnection");
    const pc = new RTCPeerConnection(iceConfig);
    
    // Debug Connection State
    pc.onconnectionstatechange = () => {
        console.log("📶 Peer Connection State:", pc.connectionState);
    };
    
    pc.oniceconnectionstatechange = () => {
        console.log("🧊 ICE Connection State:", pc.iceConnectionState);
    };

    // ✅ CRITICAL: This is where the stream is handed off to the UI
    pc.ontrack = (e) => {
        console.log("🎥 Peer received TRACK:", e.track.kind, e.streams[0]?.id);
        if (e.streams && e.streams[0]) {
            callbacksRef.current.onRemoteStream?.(e.streams[0]);
        }
    };
    
    // ✅ CRITICAL: Emits ICE candidates
    pc.onicecandidate = (e) => {
        if (e.candidate) callbacksRef.current.onIceCandidate?.(e.candidate);
    };

    pcRef.current = pc;
    return pc;
  };

  const addAnswerToQueue = (answer) => answerQueueRef.current.push(answer);

  const flushQueuedAnswers = async () => {
    const pc = pcRef.current;
    if (!pc) return;

    while (answerQueueRef.current.length) {
      const ans = answerQueueRef.current[0];
      if (pc.signalingState === "stable") {
          answerQueueRef.current.shift(); 
      } else if (pc.signalingState === "have-local-offer") {
          answerQueueRef.current.shift();
          try { 
              await pc.setRemoteDescription(ans); 
              console.log("✅ Flushed queued answer");
          } catch(e) { 
              console.error("Flush Error", e); 
          }
      } else {
          break; // Wait for state change
      }
    }
  };

  const addIceCandidateSafe = async (candidate) => {
      const pc = pcRef.current;
      if (!pc) return;
      if (pc.remoteDescription && pc.signalingState !== "closed") {
          try { await pc.addIceCandidate(candidate); } catch(e) { console.error("ICE Add Error", e); }
      }
  }

  const close = () => {
    if (pcRef.current) {
        console.log("🗑️ Closing Viewer PC");
        pcRef.current.close();
        pcRef.current = null;
    }
    answerQueueRef.current = [];
  };

  return useMemo(() => ({ 
      createPC, 
      close, 
      addAnswerToQueue, 
      flushQueuedAnswers, 
      addIceCandidateSafe 
  }), []);
};